import { useEffect, useRef } from 'react';

/**
 * HeroBackground
 * -------------------------------------------------------------------
 * Raw-WebGL background for the hero section. Loads an image as a
 * texture and renders it to a full-section canvas with:
 *   - aspect-correct "cover" UV mapping
 *   - cursor parallax (eased)
 *   - low-frequency noise displacement (breathing motion)
 *   - bronze/soft-black duotone grading
 *   - soft vignette + film grain
 *   - 1.2s fade-in reveal on mount
 *   - prefers-reduced-motion fallback (motion disabled)
 *
 * A second 2D canvas sits directly above this one and paints a
 * cursor-driven light trail (see the `.hero-trail` canvas in Hero.js).
 * This component also exposes a cursor-engagement reveal: while the
 * cursor is inside the hero, `uReveal` climbs toward 1; when it
 * leaves, it decays back to 0. The shader multiplies brightness by
 * that value so the portrait only emerges under the cursor.
 *
 * No external dependencies.
 */

const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

const FRAG = `
precision highp float;

varying vec2 vUv;

uniform sampler2D uImage;
uniform vec2  uResolution;
uniform vec2  uImageSize;
uniform vec2  uMouse;
uniform float uTime;
uniform float uReveal;          // 0..1 cursor-engagement reveal
uniform float uMountReveal;     // 0..1 1.2s fade-in on mount
uniform float uReducedMotion;

// --- Simplex noise (Ashima) -----------------------------------------------
vec3 mod289(vec3 x){ return x - floor(x * (1.0/289.0)) * 289.0; }
vec2 mod289(vec2 x){ return x - floor(x * (1.0/289.0)) * 289.0; }
vec3 permute(vec3 x){ return mod289(((x * 34.0) + 1.0) * x); }
float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439,
                     -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy));
  vec2 x0 = v -   i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m; m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x  = a0.x  * x0.x  + h.x  * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

// cheap hash-based white-noise for grain
float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }

// "cover" fit — keep image proportional, crop to section
vec2 coverUv(vec2 uv, vec2 res, vec2 img) {
  float ra = res.x / res.y;
  float ia = img.x / img.y;
  vec2  scale = (ra < ia) ? vec2(ia / ra, 1.0) : vec2(1.0, ra / ia);
  return (uv - 0.5) / scale + 0.5;
}

void main() {
  vec2 uv = coverUv(vUv, uResolution, uImageSize);

  float motion = 1.0 - uReducedMotion;

  // eased mouse parallax
  vec2 parallax = (uMouse - 0.5) * 0.035 * motion;

  // low-frequency breathing warp (subtle, slow)
  float t = uTime * 0.045 * motion;
  float ndAmp = 0.0045 * motion;
  vec2 nd = vec2(
    snoise(uv * 2.0 + vec2(t, 0.0)),
    snoise(uv * 2.0 + vec2(0.0, t))
  ) * ndAmp;

  vec2 sampleUv = clamp(uv + parallax + nd, vec2(0.001), vec2(0.999));
  vec3 col = texture2D(uImage, sampleUv).rgb;

  // duotone — map luminance between soft-black (shadows) and bronze (highlights)
  float luma = dot(col, vec3(0.299, 0.587, 0.114));
  vec3 toneShadow    = vec3(0.0706, 0.0588, 0.051);        // #120f0d  (--soft-black)
  vec3 toneHighlight = vec3(0.549,  0.416,  0.247) * 1.45; // #8c6a3f  (--bronze), lifted
  col = mix(toneShadow, toneHighlight, smoothstep(0.02, 0.95, luma));

  // soft vignette
  float d        = distance(vUv, vec2(0.5));
  float vignette = smoothstep(0.9, 0.25, d);
  col *= mix(0.4, 1.0, vignette);

  // film grain
  float g = hash(vUv * uResolution + uTime) - 0.5;
  col += g * 0.055;

  // 1.2s mount fade-in from black (keeps the canvas machinery alive
  // even when the cursor hasn't engaged yet)
  col *= uMountReveal;

  // global intensity — portrait reads as atmospheric rather than assertive
  col *= 0.72;

  // cursor-engagement reveal — portrait is invisible until the cursor
  // enters the hero; fades back to dark on leave
  col *= mix(0.0, 1.0, uReveal);

  gl_FragColor = vec4(col, 1.0);
}
`;

function compileShader(gl, type, src) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    // eslint-disable-next-line no-console
    console.error('[HeroBackground] shader error:', gl.getShaderInfoLog(s));
    gl.deleteShader(s);
    return null;
  }
  return s;
}

export default function HeroBackground({ src }) {
  const canvasRef = useRef(null);
  const trailCanvasRef = useRef(null);

  // exposed to the window so Hero.js mouse handlers can update the trail
  // without threading refs down — the component below owns all of it.
  const trailRef = useRef([]);                       // recent cursor points (pixel coords)
  const targetMouseRef = useRef({ x: -999, y: -999 }); // normalised 0..1 (or <0 when absent)
  const smoothMouseRef = useRef(null); // pixel coords, CSS px; null until first entry
  const globalRevealRef = useRef(0);                   // 0..1 cursor-engagement reveal
  const hasCursorRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const trailCanvas = trailCanvasRef.current;
    if (!canvas || !trailCanvas) return undefined;

    const gl = canvas.getContext('webgl', {
      antialias: true,
      alpha: false,
      premultipliedAlpha: false,
      powerPreference: 'high-performance',
    });
    if (!gl) return undefined; // browser falls back to the CSS .hero-bg behind it

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const vs = compileShader(gl, gl.VERTEX_SHADER, VERT);
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return undefined;

    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      // eslint-disable-next-line no-console
      console.error('[HeroBackground] link error:', gl.getProgramInfoLog(prog));
      return undefined;
    }
    gl.useProgram(prog);

    // fullscreen quad
    const posBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW,
    );
    const posLoc = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const uImage         = gl.getUniformLocation(prog, 'uImage');
    const uResolution    = gl.getUniformLocation(prog, 'uResolution');
    const uImageSize     = gl.getUniformLocation(prog, 'uImageSize');
    const uMouse         = gl.getUniformLocation(prog, 'uMouse');
    const uTime          = gl.getUniformLocation(prog, 'uTime');
    const uReveal        = gl.getUniformLocation(prog, 'uReveal');
    const uMountReveal   = gl.getUniformLocation(prog, 'uMountReveal');
    const uReducedMotion = gl.getUniformLocation(prog, 'uReducedMotion');

    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    // 1x1 warm-dark placeholder so we render before the image arrives
    gl.texImage2D(
      gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
      new Uint8Array([26, 23, 20, 255]),
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    let imgSize = [1, 1];
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.src = src;
    img.onload = () => {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      imgSize = [img.naturalWidth, img.naturalHeight];
    };

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.uniform1i(uImage, 0);
    gl.uniform1f(uReducedMotion, prefersReducedMotion ? 1.0 : 0.0);

    // --- 2D trail canvas setup ------------------------------------------
    const ctx2d = trailCanvas.getContext('2d');

    // CSS-pixel dimensions, used by the trail drawing math
    let cssW = 0;
    let cssH = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let cachedRect = null;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (!w || !h) return;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);

      trailCanvas.width = Math.round(w * dpr);
      trailCanvas.height = Math.round(h * dpr);
      // work in CSS pixels on the 2D context
      ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0);
      cssW = w;
      cssH = h;
      cachedRect = trailCanvas.getBoundingClientRect();
    };
    resize();
    window.addEventListener('resize', resize);

    // --- Pointer handling -----------------------------------------------
    // We listen on the hero <section> rather than window because the
    // cursor-engagement reveal should only fire inside the hero.
    const hero = canvas.closest('.hero') || canvas.parentElement;

    const mouse = { tx: 0.5, ty: 0.5, x: 0.5, y: 0.5 }; // for WebGL parallax

    const onMove = (e) => {
      if (!cachedRect) cachedRect = trailCanvas.getBoundingClientRect();
      const rect = cachedRect;
      if (!rect.width || !rect.height) return;
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;

      // WebGL parallax target (flip y for shader convention)
      mouse.tx = px / rect.width;
      mouse.ty = 1.0 - py / rect.height;

      // Trail inputs (CSS pixels)
      targetMouseRef.current = { x: px / rect.width, y: py / rect.height };
      trailRef.current.push({ x: px, y: py });
      if (trailRef.current.length > 40) trailRef.current.shift();

      hasCursorRef.current = true;
    };

    const onLeave = () => {
      hasCursorRef.current = false;
      targetMouseRef.current = { x: -999, y: -999 };
      smoothMouseRef.current = null;
      trailRef.current = [];
    };

    if (hero) {
      hero.addEventListener('pointermove', onMove, { passive: true });
      hero.addEventListener('pointerleave', onLeave);
    }

    // pause loop when tab is hidden
    let paused = false;
    const onVis = () => { paused = document.hidden; };
    document.addEventListener('visibilitychange', onVis);

    let rafId = 0;
    const start = performance.now();
    const tick = () => {
      if (!paused) {
        // ease WebGL parallax mouse toward target
        mouse.x += (mouse.tx - mouse.x) * 0.06;
        mouse.y += (mouse.ty - mouse.y) * 0.06;

        const t = (performance.now() - start) / 1000;
        const mountReveal = Math.min(1, t / 1.2);

        // ease cursor-engagement reveal
        if (hasCursorRef.current) {
          globalRevealRef.current = Math.min(1, globalRevealRef.current + 0.04);
        } else {
          globalRevealRef.current = Math.max(0, globalRevealRef.current - 0.015);
        }
        const reveal = prefersReducedMotion ? 1.0 : globalRevealRef.current;

        gl.uniform2f(uResolution, canvas.width, canvas.height);
        gl.uniform2f(uImageSize, imgSize[0], imgSize[1]);
        gl.uniform2f(uMouse, mouse.x, mouse.y);
        gl.uniform1f(uTime, t);
        gl.uniform1f(uReveal, reveal);
        gl.uniform1f(uMountReveal, mountReveal);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

        // --- 2D trail pass ---------------------------------------------
        ctx2d.clearRect(0, 0, cssW, cssH);

        if (hasCursorRef.current && targetMouseRef.current.x >= 0) {
          const tx = targetMouseRef.current.x * cssW;
          const ty = targetMouseRef.current.y * cssH;
          if (!smoothMouseRef.current) {
            smoothMouseRef.current = { x: tx, y: ty }; // snap on first entry, no slide-in
          } else {
            smoothMouseRef.current.x += (tx - smoothMouseRef.current.x) * 0.1;
            smoothMouseRef.current.y += (ty - smoothMouseRef.current.y) * 0.1;
          }

          const trail = trailRef.current;
          if (trail.length > 1) {
            for (let i = 0; i < trail.length; i++) {
              const p = trail[i];
              const frac = i / Math.max(1, trail.length - 1);
              const age = 1 - Math.pow(1 - frac, 2);
              const r = 50 + age * 110;
              const alpha = age * 0.16;

              const g = ctx2d.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
              g.addColorStop(0,    `rgba(240,215,155,${alpha})`);
              g.addColorStop(0.35, `rgba(160,110,45,${alpha * 0.55})`);
              g.addColorStop(1,    'rgba(0,0,0,0)');

              ctx2d.save();
              ctx2d.beginPath();
              ctx2d.arc(p.x, p.y, r, 0, Math.PI * 2);
              ctx2d.closePath();
              ctx2d.fillStyle = g;
              ctx2d.fill();
              ctx2d.restore();
            }
          }

          if (!smoothMouseRef.current) return;
          const lx = smoothMouseRef.current.x;
          const ly = smoothMouseRef.current.y;

          // spotlight at the cursor tip
          const spot = ctx2d.createRadialGradient(lx, ly, 0, lx, ly, 100);
          spot.addColorStop(0,    'rgba(252,232,175,0.32)');
          spot.addColorStop(0.25, 'rgba(200,155,65,0.18)');
          spot.addColorStop(0.6,  'rgba(120,80,20,0.07)');
          spot.addColorStop(1,    'rgba(0,0,0,0)');
          ctx2d.fillStyle = spot;
          ctx2d.fillRect(0, 0, cssW, cssH);

          // cursor indicator — filled dot + outer ring
          ctx2d.beginPath();
          ctx2d.arc(lx, ly, 3.5, 0, Math.PI * 2);
          ctx2d.fillStyle = 'rgba(240,220,160,0.55)';
          ctx2d.fill();

          ctx2d.beginPath();
          ctx2d.arc(lx, ly, 9, 0, Math.PI * 2);
          ctx2d.strokeStyle = 'rgba(140,106,63,0.35)';
          ctx2d.lineWidth = 0.5;
          ctx2d.stroke();
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      if (hero) {
        hero.removeEventListener('pointermove', onMove);
        hero.removeEventListener('pointerleave', onLeave);
      }
      document.removeEventListener('visibilitychange', onVis);

      // NOTE: intentionally NOT calling loseContext() here.
      // In React StrictMode the effect is mount→unmount→mount, and loseContext()
      // would permanently damage the context shared with the re-mount.
      // The browser GCs the context when the canvas leaves the DOM.
    };
  }, [src]);

  return (
    <>
      <canvas ref={canvasRef} className="hero-canvas" aria-hidden="true" />
      <canvas ref={trailCanvasRef} className="hero-trail" aria-hidden="true" />
    </>
  );
}
